import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { LanesBoard } from '../components/LanesBoard'
import { useAuth } from '../lib/auth'
import { usePageMeta } from '../lib/meta'
import { parseHandoff, withoutHandoff } from '../lib/session-handoff'
import { authClient, sharedAuthClient } from '../lib/supabase'

// /dashboard is where the Navo24 workspace sends a customer who owns FreightRates ("Open Freight rates").
// It carries the signed-in session in the URL fragment (see lib/session-handoff.ts). Until 2026-10-02 this
// route did not exist, so every owner who clicked through landed on a 404 even though the product was
// active on their account.
//
// The fragment is read once, taken out of the address bar before anything else, and handed to Supabase,
// which verifies it. A token that does not verify leaves the visitor signed out with a normal sign-in
// prompt, never an error page.

type Handoff = 'none' | 'working' | 'failed'

export function DashboardPage() {
  usePageMeta(
    'My lanes — FreightRatesMCP',
    'Your saved lanes with every liner rate side by side.',
    '/dashboard',
    ['en']
  )
  const { session, loading } = useAuth()
  const [handoff, setHandoff] = useState<Handoff>(() =>
    typeof window !== 'undefined' && parseHandoff(window.location.hash) ? 'working' : 'none'
  )

  // A private page: keep it out of search results.
  useEffect(() => {
    const m = document.createElement('meta')
    m.name = 'robots'
    m.content = 'noindex'
    document.head.appendChild(m)
    return () => m.remove()
  }, [])

  useEffect(() => {
    const h = parseHandoff(window.location.hash)
    if (!h) return
    // Remove the tokens from the address bar and history entry first, whatever happens next.
    const { pathname, search, hash } = window.location
    window.history.replaceState(null, '', withoutHandoff(pathname, search, hash))
    // The workspace's session is a SHARED-project session: with shared sign-in on it is adopted there
    // (the identity project refuses it, which is why this was inert before).
    ;(sharedAuthClient ?? authClient).auth
      .setSession(h)
      .then(({ error }) => setHandoff(error ? 'failed' : 'none'))
      .catch(() => setHandoff('failed'))
  }, [])

  if (loading || handoff === 'working') {
    return (
      <div className="mx-auto max-w-md px-5 py-24 text-center text-muted">Opening your lanes…</div>
    )
  }

  if (!session) {
    return (
      <div className="mx-auto max-w-md px-5 py-24 text-center">
        <h1 className="font-display text-3xl font-light">My lanes</h1>
        <p className="mt-3 text-sm text-muted">
          {handoff === 'failed'
            ? 'Your session could not be carried over from the workspace. Sign in to open your lanes.'
            : 'Sign in to open your lanes.'}
        </p>
        <Link
          to="/account"
          className="mt-6 inline-block rounded-lg bg-signal px-5 py-2.5 text-sm font-semibold text-on-accent"
        >
          Sign in
        </Link>
      </div>
    )
  }

  return <LanesBoard />
}
